import { signalStore, type, withState, withMethods, patchState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, Observable, catchError, from, map, of, pipe, switchMap, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { TOperationException, TOpeningHours, TPersonSettings, TSpace, TSpaceType, TPrintReceiptCount, TSpooler, TActivities, TCategory, TAverageTicket } from '../entities/settings.model';
import { GeneralService } from '../infra/general.service';
import { ErrorStore } from '@application/errors/data-access';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@application/data-access';
import {
  addEntity,
  entityConfig,
  removeEntity,
  setEntities,
  updateEntity,
  withEntities
} from '@ngrx/signals/entities';
import { DxOdataStoreService } from '@shared/components/dx-data-grid/data-access';
import { APP_CONFIG } from '@application/config';
import DataSource from 'devextreme/data/data_source';
import { TActiveResourceById } from '../entities/settings.interface';
import { tapResponse } from '@ngrx/operators';
import { SelfUserStore, PermissionNumber } from '@person/user/self/data-access';

const TEMP_AUTH_PERMISSION_GUARD_PATH = 'TEMPORARY_AUTH_MODAL.PERMISSION_GUARD';

export type TGeneralStore = {
  clientsAndVisitors: TPersonSettings;
  openingHours: TOpeningHours;
  validOperationPeriods: boolean;
  validOperationException: boolean;
  spaceTypes: TSpaceType[];
  activity: TActivities;
  receivableSettings: TPrintReceiptCount;
  activeGroupsBySpaceId: TActiveResourceById[];
  activeBookingsBySpaceId: TActiveResourceById[];
  activeBookingsByActivityId: TActiveResourceById[];
  spoolers: TSpooler[];
  pairingCode: string;
  categories: TCategory[];
  averageTicketSettings: TAverageTicket;
};

export const initialGeneralStore: TGeneralStore = {
  clientsAndVisitors: {
    PersonSettingsId: null,
    EmailRequired: false,
    RegistrationRequired: false,
    FiscalIdentityRequired: false,
    IdentityDocumentRequired: false,
    AutomaticDeactivationDays: 0,
    DisableWithoutSubscriptionDays: 0,
    UpdatedOn: null
  },
  openingHours: null,
  validOperationPeriods: false,
  validOperationException: false,
  spaceTypes: null,
  activity: null,
  receivableSettings: null,
  activeGroupsBySpaceId: null,
  activeBookingsBySpaceId: null,
  activeBookingsByActivityId: null,
  spoolers: null,
  pairingCode: null,
  categories: null,
  averageTicketSettings: null
};

const configOperationsExceptions = entityConfig({
  entity: type<TOperationException>(),
  collection: 'operationsExceptions',
  selectId: (operation: TOperationException) => operation.OperationExceptionId
});

const configSpaces = entityConfig({
  entity: type<TSpace>(),
  collection: 'spaces',
  selectId: (space: TSpace) => space.SpaceId
});

const configActivities = entityConfig({
  entity: type<TActivities>(),
  collection: 'activities',
  selectId: (activities: TActivities) => activities.ActivityId
});

export const GeneralSettingsStore = signalStore(
  { providedIn: 'root' },
  withState<TGeneralStore>(initialGeneralStore),
  withEntities(configOperationsExceptions),
  withEntities(configSpaces),
  withEntities(configActivities),
  withMethods(
    (store,
      generalService = inject(GeneralService),
      errorStore = inject(ErrorStore),
      dxOdataStoreService = inject(DxOdataStoreService),
      url = inject(APP_CONFIG).apiUrl,
      selfUserStore = inject(SelfUserStore),
    ) => {
      const ensureAdjustPermission = () =>
        from(
          selfUserStore.ensurePermissions(
            [PermissionNumber.AjusteAtualizar],
            {
              title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.ADJUST_SETTINGS.TITLE`,
              message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.ADJUST_SETTINGS.MESSAGE`,
            },
          ),
        );

      const guardAdjustPermission = <T>(requestKey: string, work: () => Observable<T>) =>
        ensureAdjustPermission().pipe(
          switchMap((hasPermission) => {
            if (!hasPermission) {
              patchState(store, setPropInit(requestKey));
              return EMPTY;
            }

            return work();
          }),
        );

      const createPairingCode = rxMethod<void>(pipe(
        tap(() => patchState(store, setPropLoading('createPairingCode'))),
        switchMap(() => guardAdjustPermission('createPairingCode', () => generalService.getPairingCode().pipe(
          tapResponse({
            next: (response) => {
              patchState(store, { pairingCode: response.PairingCode }, setPropLoaded('createPairingCode'), setPropLoaded('loadSpoolers'));
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('createPairingCode', error), setPropError('loadSpoolers', error));
            }
          })
        )))
      ));
      const loadSpoolers = rxMethod<string>(pipe(
        tap(() => patchState(store, setPropLoading('loadSpoolers'))),
        switchMap((tenantId) => generalService.getSpoolers(tenantId).pipe(
          tapResponse({
            next: (response) => {
              let pairingCode: string;
              const spoolers = response.reduce((acc, spooler) => {
                if (!spooler.DeviceName) {
                  pairingCode = spooler.PairingCode;
                } else {
                  acc.push({ ...spooler });
                }
                return acc;
              }, []);

              if (pairingCode) {
                patchState(store, { spoolers, pairingCode }, setPropLoaded('loadSpoolers'));
                return;
              }

              patchState(store, { spoolers }, setPropLoaded('loadSpoolers'));
              createPairingCode();
            },
            error: (error: HttpErrorResponse) => {
              patchState(store, setPropError('loadSpoolers', error));
            }
          })
        ))
      ));
      return {
        getSpaces(): DataSource {
          return dxOdataStoreService.fromOdata({
            url: url.odata_account_service,
            metadata: 'Spaces',
            key: 'SpaceId',
          });
        },
        loadCustomersAndVisitors: rxMethod<void>(pipe(
          tap(() => patchState(store, setPropLoading('loadClientsAndVisitors'))),
          switchMap(() => generalService.getPersonSettings().pipe(
            tapResponse({
              next: (response: TPersonSettings) =>
                patchState(store, { clientsAndVisitors: response }, setPropLoaded('loadClientsAndVisitors')),
              error: (error: HttpErrorResponse) => {
                patchState(store, setPropError('loadClientsAndVisitors', error));
              }
            })
          ))
        )),
        updateCustomersAndVisitors: rxMethod<Partial<TPersonSettings>>(pipe(
          tap(() => patchState(store, setPropLoading('updateClientAndVisitors'))),
          switchMap((newSettings) => guardAdjustPermission('updateClientAndVisitors', () => generalService.updatePersonSettings(newSettings).pipe(
              tapResponse({
                next: (newSettings) => (patchState(store, { clientsAndVisitors: newSettings }, setPropLoaded('updateClientAndVisitors'))),
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('updateClientAndVisitors', error));
                }
              })
            )))
        )),
        loadOperationsExceptions: rxMethod<void>(pipe(
          tap(() => patchState(store, setPropLoading('loadOperationsExceptions'))),
          switchMap(() => generalService.getOperationsExceptions().pipe(
            map((response) => {
              patchState(store, setEntities(response, configOperationsExceptions), setPropLoaded('loadOperationsExceptions'));
            })
          ))
        )),
        updateOperationsExceptions: rxMethod<Partial<TOperationException>>(pipe(
          tap(() => patchState(store, setPropLoading('updateOperationsExceptions'))),
          switchMap((data) => guardAdjustPermission('updateOperationsExceptions', () => generalService.updateOperationsExceptions(data).pipe(
              tap((response: TOperationException) => (patchState(store, updateEntity({ id: response.OperationExceptionId, changes: response }, configOperationsExceptions), setPropLoaded('updateOperationsExceptions')))),
              catchError((error) => {
                patchState(store, setPropError('updateOperationsExceptions', error))
                return of(error);
              })
            )))
        )),
        removeOperationsExceptions: rxMethod<Partial<TOperationException>>(pipe(
          tap(() => patchState(store, setPropLoading('removeOperationsExceptions'))),
          switchMap((removedOperation) => guardAdjustPermission('removeOperationsExceptions', () => generalService.removeOperationsExceptions(removedOperation).pipe(
              tap(() => (patchState(store, removeEntity(removedOperation.OperationExceptionId, configOperationsExceptions), setPropLoaded('removeOperationsExceptions')))),
              catchError((error) => {
                patchState(store, setPropError('removeOperationsExceptions', error))
                return of(error);
              })
            )))
        )),
        addOperationsExceptions: rxMethod<Partial<TOperationException>>(pipe(
          tap(() => patchState(store, setPropLoading('addOperationsExceptions'))),
          switchMap((newSettings) => guardAdjustPermission('addOperationsExceptions', () => {
            delete newSettings.OperationExceptionId;
            delete newSettings.IsImported;
            newSettings.IsOpen = !!newSettings.IsOpen;
            return generalService.addOperationsExceptions(newSettings).pipe(
              tap((response) => (patchState(store, addEntity(response, configOperationsExceptions), setPropLoaded('addOperationsExceptions')))),
              catchError((error) => {
                patchState(store, setPropError('addOperationsExceptions', error))
                return of(error);
              })
            );
          }))
        )),
        loadOpeningHours: rxMethod<void>(pipe(
          tap(() => patchState(store, setPropLoading('loadOpeningHours'))),
          switchMap(() => generalService.getOpeningHours().pipe(
            tapResponse({
              next: (response: TOpeningHours) => {
                patchState(store, { openingHours: response }, setPropLoaded('loadOpeningHours'));
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, setPropError('loadOpeningHours', error));
              }
            })
          ))
        )),
        getActivitiesDataSource: () => {
          return dxOdataStoreService.fromOdata({
            url: url.odata_activity_service, metadata: 'Activities', key: 'ActivityId',
            params: {
              $count: true,
              $orderby: 'Description',
            }
          })
        },
        addActivities: rxMethod<Partial<TActivities>>(pipe(
          tap(() => patchState(store, setPropLoading('addActivities'))),
          switchMap((newSpace) => guardAdjustPermission('addActivities', () => {
            delete newSpace.ActivityId;
            return generalService.addActivities(newSpace).pipe(
              tap((response) => {
                patchState(store, setPropLoaded('addActivities'));
              }),
              catchError((error) => {
                patchState(store, setPropError('addActivities', error))
                return of(error);
              })
            );
          }))
        )),
        getActivityById: rxMethod<string>(pipe(
          tap(() => patchState(store, setPropLoading('getActivityById'))),
          switchMap((activityId) => generalService.getActivityById(activityId).pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { activity: response }, setPropLoaded('getActivityById'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('getActivityById', error));
              }
            })
          ))
        )),
        removeActivities: rxMethod<string>(pipe(
          tap(() => patchState(store, setPropLoading('removeActivities'))),
          switchMap((activityId) => guardAdjustPermission('removeActivities', () => generalService.removeActivities(activityId).pipe(
              tap(() => {
                patchState(store, setPropLoaded('removeActivities'));
              }),
              catchError((error) => {
                patchState(store, setPropError('removeActivities', error))
                return of(error);
              })
            )))
        )),
        updateActivities: rxMethod<Partial<TActivities>>(pipe(
          tap(() => patchState(store, setPropLoading('updateActivities'))),
          switchMap((updatedActivities) => guardAdjustPermission('updateActivities', () => generalService.updateActivities(updatedActivities).pipe(
              tap(() => {
                patchState(store, setPropLoaded('updateActivities'));
              }),
              catchError((error) => {
                patchState(store, setPropError('updateActivities', error))
                return of(error);
              })
            )))
        )),
        updateOpeningHours: rxMethod<Partial<TOpeningHours>>(pipe(
          tap(() => patchState(store, setPropLoading('updateOpeningHours'))),
          switchMap((newSettings) => guardAdjustPermission('updateOpeningHours', () => generalService.updateOpeningHours(newSettings).pipe(
              tapResponse({
                next: (newSettings) => {
                  patchState(store, { openingHours: newSettings }, setPropLoaded('updateOpeningHours'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('updateOpeningHours', error));
                }
              })
            )))
        )),
        loadSpaces: rxMethod<void>(pipe(
          tap(() => patchState(store, setPropLoading('loadSpaces'))),
          switchMap(() => generalService.getSpaces().pipe(
            tapResponse({
              next: (response) => {
                patchState(
                  store,
                  setEntities(response.value, configSpaces),
                  setPropLoaded('loadSpaces')
                );
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('loadSpaces', error));
              }
            })
          ))
        )),
        addSpace: rxMethod<Partial<TSpace>>(pipe(
          tap(() => patchState(store, setPropLoading('addSpace'))),
          switchMap((newSpace) => guardAdjustPermission('addSpace', () => {
            delete newSpace.SpaceId;
            return generalService.addSpace(newSpace).pipe(
              tap((response) => {
                patchState(store, addEntity(response, configSpaces), setPropLoaded('addSpace'));
              }),
              catchError((error) => {
                patchState(store, setPropError('addSpace', error))
                return of(error);
              })
            );
          }))
        )),
        updateSpace: rxMethod<Partial<TSpace>>(pipe(
          tap(() => patchState(store, setPropLoading('updateSpace'))),
          switchMap((updatedSpace) => guardAdjustPermission('updateSpace', () => generalService.updateSpace(updatedSpace).pipe(
              tap((response) => {
                patchState(
                  store,
                  updateEntity({ id: response.SpaceId, changes: response }, configSpaces),
                  setPropLoaded('updateSpace')
                );
              }),
              catchError((error) => {
                patchState(store, setPropError('updateSpace', error))
                return of(error);
              })
            )))
        )),
        removeSpace: rxMethod<string>(pipe(
          tap(() => patchState(store, setPropLoading('removeSpace'))),
          switchMap((spaceId) => guardAdjustPermission('removeSpace', () => generalService.removeSpace(spaceId).pipe(
              tap(() => {
                patchState(store, removeEntity(spaceId, configSpaces), setPropLoaded('removeSpace'));
              }),
              catchError((error) => {
                patchState(store, setPropError('removeSpace', error))
                return of(error);
              })
            )))
        )),
        loadSpaceTypes: rxMethod<void>(pipe(
          tap(() => patchState(store, setPropLoading('getSpaceTypes'))),
          switchMap(() => generalService.getSpaceTypes().pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { spaceTypes: response }, setPropLoaded('getSpaceTypes'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('getSpaceTypes', error));
              }
            })
          ))
        )),
        loadReceivableSettings: rxMethod<void>(pipe(
          tap(() => patchState(store, setPropLoading('loadReceivableSettings'))),
          switchMap(() => generalService.getPrintReceiptCount().pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { receivableSettings: response }, setPropLoaded('loadReceivableSettings'));
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, setPropError('loadReceivableSettings', error));
              }
            })
          ))
        )),
        updateReceivableSettings: rxMethod<Partial<TPrintReceiptCount>>(pipe(
          tap(() => patchState(store, setPropLoading('updateReceivableSettings'))),
          switchMap((newSettings) => guardAdjustPermission('updateReceivableSettings', () => generalService.updatePrintReceiptCount(newSettings).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, { receivableSettings: response }, setPropLoaded('updateReceivableSettings'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('updateReceivableSettings', error));
                }
              })
            )))
        )),
        loadSpoolers,
        updateSpooler: rxMethod<TSpooler>(pipe(
          tap(() => patchState(store, setPropLoading('updateSpooler'))),
          switchMap((updatedSpooler) => guardAdjustPermission('updateSpooler', () => generalService.updateSpooler(updatedSpooler).pipe(
              tapResponse({
                next: () => {
                  const currentSpoolers = store.spoolers() ?? [];
                  const updatedSpoolers = currentSpoolers.map(s => 
                    s.DeviceId === updatedSpooler.DeviceId ? updatedSpooler : s
                  );
                  patchState(store, { spoolers: updatedSpoolers }, setPropLoaded('updateSpooler'));
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('updateSpooler', error));
                }
              })
            )))
        )),
        updateSpoolerWithReturn: (updatedSpooler: TSpooler): Observable<void> => {
          patchState(store, setPropLoading('updateSpooler'));
          return guardAdjustPermission('updateSpooler', () => generalService.updateSpooler(updatedSpooler).pipe(
            tapResponse({
              next: () => {
                const currentSpoolers = store.spoolers() ?? [];
                const updatedSpoolers = currentSpoolers.map(s => 
                  s.DeviceId === updatedSpooler.DeviceId ? updatedSpooler : s
                );
                patchState(store, { spoolers: updatedSpoolers }, setPropLoaded('updateSpooler'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('updateSpooler', error));
              }
            })
          ));
        },
        removeSpooler: rxMethod<string>(pipe(
          tap(() => patchState(store, setPropLoading('removeSpooler'))),
          switchMap((spoolerId) => guardAdjustPermission('removeSpooler', () => generalService.removeSpooler(spoolerId).pipe(
              tapResponse({
                next: () => {
                  patchState(store, setPropLoaded('removeSpooler'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('removeSpooler', error));
                }
              })
            )))
        )),

        validateOperationPeriods: rxMethod<TOpeningHours>(pipe(
          switchMap((operationPeriod) =>
            guardAdjustPermission('validateOperationPeriods', () => {
              patchState(store, setPropLoading('validateOperationPeriods'));

              return generalService.validateOperationPeriods(operationPeriod).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, { validOperationPeriods: response }, setPropLoaded('validateOperationPeriods'));
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, setPropError('validateOperationPeriods', error));
                  }
                })
              );
            })
          )
        )),

        cancelBookingByOperationPeriod: rxMethod<{operationPeriod: TOpeningHours, confirmation: string}>(pipe(
          switchMap(({operationPeriod, confirmation}) =>
            guardAdjustPermission('cancelBookingByOperationPeriod', () => {
              patchState(store, setPropLoading('cancelBookingByOperationPeriod'));

              return generalService.cancelBookingByOperationPeriod(operationPeriod, confirmation).pipe(
                tapResponse({
                  next: () => {
                    patchState(store, setPropLoaded('cancelBookingByOperationPeriod'));
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, setPropError('cancelBookingByOperationPeriod', error));
                  }
                })
              );
            })
          )
        )),

        validateOperationException: rxMethod<{day: string, isOpen: boolean}>(pipe(
          switchMap(({day, isOpen}) =>
            guardAdjustPermission('validateOperationException', () => {
              patchState(store, setPropLoading('validateOperationException'));

              return generalService.validateOperationException(day, isOpen).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, { validOperationException: response }, setPropLoaded('validateOperationException'));
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, setPropError('validateOperationException', error));
                  }
                })
              );
            })
          )
        )),

        cancelBookingsByOperationException: rxMethod<{day: string, confirmation: string}>(pipe(
          switchMap(({day, confirmation}) =>
            guardAdjustPermission('cancelBookingsByOperationException', () => {
              patchState(store, setPropLoading('cancelBookingsByOperationException'));

              return generalService.cancelBookingsByOperationException(day, confirmation).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(store, { validOperationException: response }, setPropLoaded('cancelBookingsByOperationException'));
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, setPropError('cancelBookingsByOperationException', error));
                  }
                })
              );
            })
          )
        )),

        getActiveGroupsBySpaceId: rxMethod<string>(pipe(
          tap(() => patchState(store, setPropLoading('getActiveGroupsBySpaceId'))),
          switchMap((spaceId) => {
            return generalService.getActiveGroupsBySpaceId(spaceId).pipe(
              tapResponse({
                next: (activeGroupsBySpaceId) => {
                  patchState(store, { activeGroupsBySpaceId }, setPropLoaded('getActiveGroupsBySpaceId'));
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('getActiveGroupsBySpaceId', error));
                }
              })
            );
          })
        )),

        getActiveBookingsBySpaceId: rxMethod<string>(pipe(
          tap(() => patchState(store, setPropLoading('getActiveBookingsBySpaceId'))),
          switchMap((spaceId) => {
            return generalService.getActiveBookingsBySpaceId(spaceId).pipe(
              tapResponse({
                next: (activeBookingsBySpaceId) => {
                  patchState(store, { activeBookingsBySpaceId }, setPropLoaded('getActiveBookingsBySpaceId'));
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('getActiveBookingsBySpaceId', error));
                }
              })
            );
          })
        )),

        getActiveBookingsByActivityId: rxMethod<string>(pipe(
          tap(() => patchState(store, setPropLoading('getActiveBookingsByActivityId'))),
          switchMap((activityId) => {
            return generalService.getActiveBookingsByActivityId(activityId).pipe(
              tapResponse({
                next: (activeBookingsByActivityId) => {
                  patchState(store, { activeBookingsByActivityId }, setPropLoaded('getActiveBookingsByActivityId'));
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('getActiveBookingsByActivityId', error));
                }
              })
            );
          })
        )),

        resetSpaceResources: () => {
          patchState(store, {
            activeBookingsBySpaceId: null,
            activeGroupsBySpaceId: null
          }, setPropInit('getActiveBookingsBySpaceId', 'getActiveGroupsBySpaceId'));
        },

        loadCategories: rxMethod<void>(pipe(
          tap(() => patchState(store, setPropLoading('loadCategories'))),
          switchMap(() => generalService.getCategories().pipe(
            tapResponse({
              next: (categories) => {
                patchState(store, { categories }, setPropLoaded('loadCategories'));
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, setPropError('loadCategories', error));
              }
            })
          ))
        )),

        createCategory: rxMethod<Omit<TCategory, 'FinancialCategoryId'>>(pipe(
          tap(() => patchState(store, setPropLoading('createCategory'))),
          switchMap((category) => {
            return generalService.createCategory(category).pipe(
              tapResponse({
                next: () => {
                  patchState(store, setPropLoaded('createCategory'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('createCategory', error));
                }
              })
            );
          })
        )),

        updateCategory: rxMethod<Partial<TCategory>>(pipe(
          tap(() => patchState(store, setPropLoading('updateCategory'))),
          switchMap((category) => {
            return generalService.updateCategory(category).pipe(
              tapResponse({
                next: () => {
                  patchState(store, setPropLoaded('updateCategory'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('updateCategory', error));
                }
              })
            );
          })
        )),

        deleteCategory: rxMethod<string>(pipe(
          tap(() => patchState(store, setPropLoading('deleteCategory'))),
          switchMap((id) => {
            return generalService.deleteCategory(id).pipe(
              tapResponse({
                next: () => {
                  patchState(store, setPropLoaded('deleteCategory'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('deleteCategory', error));
                }
              })
            );
          })
        )),

        loadAverageTicketSettings: rxMethod<void>(pipe(
          tap(() => patchState(store, setPropLoading('loadAverageTicketSettings'))),
          switchMap(() => generalService.getAverageTicketSettings().pipe(
            tapResponse({
              next: (averageTicketSettings) => {
                patchState(store, { averageTicketSettings }, setPropLoaded('loadAverageTicketSettings'));
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, setPropError('loadAverageTicketSettings', error));
              }
            })
          ))
        )),

        updateAverageTicketSettings: rxMethod<TAverageTicket>(pipe(
          tap(() => patchState(store, setPropLoading('updateAverageTicketSettings'))),
          switchMap((settings) => {
            return generalService.updateAverageTicketSettings(settings).pipe(
              tapResponse({
                next: (averageTicketSettings) => {
                  patchState(store, { averageTicketSettings }, setPropLoaded('updateAverageTicketSettings'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('updateAverageTicketSettings', error));
                }
              })
            );
          })
        )),

        resetActivityReqState: () => {
          patchState(store, {activity: null}, setPropInit('getActivityById', 'removeActivities', 'updateActivities', 'addActivities'));
        },

        resetReqState: () => {
          patchState(store, setPropInit(
            'loadClientsAndVisitors',
            'updateClientAndVisitors',
            'loadOpeningHours',
            'updateOpeningHours',
            'loadOperationsExceptions',
            'updateOperationsExceptions',
            'removeOperationsExceptions',
            'addOperationsExceptions',
            'loadSpaces',
            'loadActivities',
            'addActivities',
            'addSpace',
            'updateSpace',
            'removeSpace',
            'removeActivities',
            'updateActivities',
            'getSpaceTypes',
            'loadReceivableSettings',
            'updateReceivableSettings',
            'loadSpoolers',
            'updateSpooler',
            'createPairingCode',
            'removeSpooler',
            'validateOperationPeriods',
            'cancelBookingByOperationPeriod',
            'validateOperationException',
            'cancelBookingsByOperationException',
            'getActiveGroupsBySpaceId',
            'getActiveBookingsBySpaceId',
            'getActiveBookingsByActivityId',
            'loadCategories',
            'createCategory',
            'updateCategory',
            'deleteCategory',
            'loadAverageTicketSettings',
            'updateAverageTicketSettings'
          ));
        }
      };
    }
  ),
  withReqState(
    'loadClientsAndVisitors',
    'updateClientAndVisitors',
    'loadOpeningHours',
    'updateOpeningHours',
    'loadOperationsExceptions',
    'updateOperationsExceptions',
    'removeOperationsExceptions',
    'addOperationsExceptions',
    'loadSpaces',
    'loadActivities',
    'addSpace',
    'updateActivities',
    'addActivities',
    'removeActivities',
    'updateSpace',
    'removeSpace',
    'getSpaceTypes',
    'loadReceivableSettings',
    'updateReceivableSettings',
    'loadSpoolers',
    'updateSpooler',
    'createPairingCode',
    'removeSpooler',
    'validateOperationPeriods',
    'cancelBookingByOperationPeriod',
    'validateOperationException',
    'cancelBookingsByOperationException',
    'getActiveGroupsBySpaceId',
    'getActiveBookingsBySpaceId',
    'getActiveBookingsByActivityId',
    'loadCategories',
    'createCategory',
    'updateCategory',
    'deleteCategory',
    'loadAverageTicketSettings',
    'updateAverageTicketSettings',
    'getActivityById'
  )
);
